import { pipe, UnaryFunction, Observable } from 'rxjs'
import { filter, take } from 'rxjs/operators'

import {
  ActionReducerMap,
  createFeatureSelector,
  createSelector,
  MetaReducer,
  select,
  MemoizedSelector,
} from '@ngrx/store'

import { environment } from '../../environments/environment'
import * as fromUser from './reducers/user.reducer'
import * as fromCenter from './reducers/center.reducer'
import * as fromFlightNetwork from './reducers/flight-network.reducer'
import * as fromReleaseInfo from './reducers/release-info.reducer'

export interface AppState {
  user: fromUser.State
  center: fromCenter.State
  flightNetwork: fromFlightNetwork.State
  releaseInfo: fromReleaseInfo.State
}

export const reducers: ActionReducerMap<AppState> = {
  user: fromUser.reducer,
  center: fromCenter.reducer,
  flightNetwork: fromFlightNetwork.reducer,
  releaseInfo: fromReleaseInfo.reducer,
}

export const metaReducers: MetaReducer<AppState>[] = !environment.production ? [] : []

/**
 * actions
 */
export * from './actions/user.actions'
export * from './actions/center.actions'
export * from './actions/flight-network.actions'

export { DEFAULT_AVATAR_URL, PermissionTypes, UserPermissionsKeyTypes } from './reducers/user.reducer'
export type { UserState } from './reducers/user.reducer'
export type {
  FlightNetworkEdgeState,
  FlightNetworkOperatorState,
  FlightNetworkNodeState,
  FlightNetworkState,
  FlightNetworkCenterState,
  ChecklistState,
  VehicleState,
} from './reducers/flight-network.reducer'
export { CenterStatus } from './reducers/flight-network.reducer'

export type { State as ReleaseInfoState } from './reducers/release-info.reducer'

/**
 * selectors
 */
const getSelectorOnce = <S, T>(selector: MemoizedSelector<S, T>): UnaryFunction<Observable<S>, Observable<T>> =>
  pipe(select(selector), filter<T>(Boolean), take(1))

// user selectors
const getUsersState = createFeatureSelector<fromUser.State>('user')
export const getUser = createSelector(getUsersState, fromUser.selectors.getUser)
export const getUserOnce = getSelectorOnce(getUser)
export const getUserAvatarUrl = createSelector(getUsersState, fromUser.selectors.getUserAvatarUrl)
export const getUserLoading = createSelector(getUsersState, fromUser.selectors.getUserLoading)
export const getUserError = createSelector(getUsersState, fromUser.selectors.getUserError)
export const canUser = createSelector(getUsersState, fromUser.selectors.canUser)

// center selectors
const getUserCenterState = createFeatureSelector<fromCenter.State>('center')
export const getUserCenter = createSelector(getUserCenterState, fromCenter.selectors.getCenter)
export const getUserCenterOnce = getSelectorOnce(getUserCenter)
export const getUserCenterStatus = createSelector(getUserCenterState, fromCenter.selectors.getCenterStatus)
export const getUserCenterStatusOnce = getSelectorOnce(getUserCenterStatus)
export const getUserCenterLoading = createSelector(getUserCenterState, fromCenter.selectors.getCenterLoading)
export const getUserCenterActiveVehicle = createSelector(getUserCenterState, fromCenter.selectors.getCenterVehicle)

// flight network selectors
/**
 * Currently center state is part of flight network state.
 * Thats why selectors of center moved here. In future it can be changed.
 */
const getFlightNetworkState = createFeatureSelector<fromFlightNetwork.State>('flightNetwork')

export const getUserCenterNode = createSelector(getFlightNetworkState, fromFlightNetwork.selectors.getNetworkCenter)
export const getUserCenterNodeOnce = getSelectorOnce(getUserCenterNode)
export const getUserCenterLatLng = createSelector(
  getFlightNetworkState,
  fromFlightNetwork.selectors.getNetworkCenterLatLng,
)
export const getUserCenterLatLngOnce = getSelectorOnce(getUserCenterLatLng)

export const getFlightNetwork = createSelector(getFlightNetworkState, fromFlightNetwork.selectors.getNetwork)
export const getFlightNetworkLoading = createSelector(
  getFlightNetworkState,
  fromFlightNetwork.selectors.getNetworkLoading,
)
export const getFlightNetworkError = createSelector(getFlightNetworkState, fromFlightNetwork.selectors.getNetworkError)
export const getFlightNetworkCenters = createSelector(
  getFlightNetworkState,
  fromFlightNetwork.selectors.getNetworkCenters,
)
export const getFlightNetworkCenterById = createSelector(
  getFlightNetworkState,
  fromFlightNetwork.selectors.getNetworkCenterById,
)

// release info selectors
const getReleaseInfoState = createFeatureSelector<fromReleaseInfo.State>('releaseInfo')
export const getReleaseInfo = createSelector(getReleaseInfoState, fromReleaseInfo.selectors.getReleaseInfo)
